/**
 * useMenuPermission Hook - Updated for Centralized RBAC
 * Menu permission kontrolleri merkezi RBAC matrisinden alınır
 *
 * Kullanım:
 * const { filteredMenuItems } = useMenuPermission();
 *
 * Eski fonksiyonları compat için tutuyoruz:
 * const { canView, canCreate } = useMenuPermission('orders');
 */

import { useAuth } from '../contexts/AuthContext';
import { useMemo, useCallback } from 'react';
import { MENU_MATRIX, MenuItemConfig } from '../config/menuMatrix';
import { hasPermission, Role } from '../config/roles';

interface PermissionState {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  isLoading: boolean;
}

interface MenuPermissionReturn extends PermissionState {
  filteredMenuItems?: MenuItemConfig[];
  canViewMenu?: (menuId: string) => boolean;
}

/**
 * Menu item'ları RBAC matrisine göre filtrele (recursive)
 */
function filterMenuByPermissions(
  items: MenuItemConfig[],
  userRole: Role
): MenuItemConfig[] {
  return items
    .filter((item) => {
      const action = item.requiredAction || 'view';
      return hasPermission(userRole, item.resource, action);
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterMenuByPermissions(item.children, userRole)
        : undefined,
    }))
    .filter((item) => {
      // Parent menü child kalmadıysa gizle (ama href varsa göster)
      if (item.children && item.children.length === 0) {
        return item.href !== undefined;
      }
      return true;
    });
}

/**
 * Merkezi RBAC matrisi kullanarak menu permissions kontrolü
 * COMPAT: Eski kod da çalışacak şekilde design edilmiş
 *
 * Usage (New):
 * const { filteredMenuItems } = useMenuPermission();
 *
 * Usage (Old - Backward Compat):
 * const { canView, canCreate } = useMenuPermission('orders');
 */
export const useMenuPermission = (menuName?: string): MenuPermissionReturn => {
  const { user, isLoading } = useAuth();
  const userRole = (user?.role || 'calisan') as Role;

  // Yeni: Filtered menu items (tüm menüler)
  const filteredMenuItems = useMemo(() => {
    if (isLoading || !user) return [];
    return filterMenuByPermissions(MENU_MATRIX, userRole);
  }, [userRole, isLoading, user]);

  // Eski compat: Tek menü item'ı için permission kontrolü
  const checkPermission = useCallback(
    (action: string): boolean => {
      if (isLoading) {
        return false;
      }

      if (!user) {
        return false;
      }

      if (!menuName) {
        return false;
      }

      // MenuMatrix'ten menü adını resource'a map et
      const findResourceByLabel = (label: string): string | null => {
        const flatMenus = flattenMenuItems(MENU_MATRIX);
        const menuItem = flatMenus.find((item) => item.label === label);
        return menuItem ? menuItem.resource : null;
      };

      // Legacy: menu name'i resource'a map et
      const resource = findResourceByLabel(menuName) || (menuName as any);

      // Eski API: 'view', 'create', 'update', 'delete' actions
      // Yeni API ile map et
      const actionMap: Record<string, any> = {
        view: 'view',
        create: 'create',
        update: 'edit',
        delete: 'delete',
      };

      const mappedAction = actionMap[action] || action;

      const hasPerms = hasPermission(userRole, resource, mappedAction);

      return hasPerms;
    },
    [user, menuName, userRole, isLoading]
  );

  const canViewMenu = (menuId: string): boolean => {
    const flatMenus = flattenMenuItems(filteredMenuItems);
    return flatMenus.some((item) => item.id === menuId);
  };

  return {
    // Eski API (backward compat)
    canView: checkPermission('view'),
    canCreate: checkPermission('create'),
    canUpdate: checkPermission('update'),
    canDelete: checkPermission('delete'),
    isLoading,

    // Yeni API
    filteredMenuItems,
    canViewMenu,
  };
};

/**
 * Menu items'ı flatten et
 */
function flattenMenuItems(items: MenuItemConfig[]): MenuItemConfig[] {
  const result: MenuItemConfig[] = [];
  items.forEach((item) => {
    result.push(item);
    if (item.children) {
      result.push(...flattenMenuItems(item.children));
    }
  });
  return result;
}

/**
 * Legacy helper - backward compatibility
 */
export const useActionPermission = (
  menuName: string,
  action: 'create' | 'update' | 'delete'
) => {
  const { canCreate, canUpdate, canDelete } = useMenuPermission(menuName);

  switch (action) {
    case 'create':
      return canCreate;
    case 'update':
      return canUpdate;
    case 'delete':
      return canDelete;
    default:
      return false;
  }
};
